/**
 * Search builder — one funnel step card: a name, mode/match controls, condition
 * rows with inline editors, the three-way count strip (matched / dropped / no
 * answer, each clickable into the drill-in on that group), what happens to the
 * people with no answer, and the first warning as a sentence. Laptop-dense per
 * the CRM density bar.
 *
 * Words follow docs/crm-outreach-lists-plan.md §A6: "M of N kept", "15%
 * answered", "People with no answer: keep them / drop them". A step that isn't
 * about a form question says "nothing on file" instead — "15% answered" would be
 * nonsense for a BMI step.
 */
import { useState } from 'react';
import { AlertTriangle, ChevronDown, ChevronUp, Pencil, Trash2, X } from 'lucide-react';
import type { CrmForm, SearchCondition, SearchPreviewStep, SearchStepDef, StudyOption, TeamMember } from '../../types';
import { fieldCls } from '../filters';
import CriterionEditor from './CriterionEditor';
import CriterionPalette from './CriterionPalette';
import type { DrillBucket } from './StepDrillIn';
import {
  describeCondition, describeStep, droppedWithData, isConditionReady, isQuestionStep, unknownWords, warningSummary,
  type DescribeCtx,
} from '../../pages/search/searchLib';

interface Props {
  step: SearchStepDef;
  stats: SearchPreviewStep | null;
  index: number;
  total: number;
  onChange: (next: SearchStepDef) => void;
  onRemove: () => void;
  onMove: (dir: -1 | 1) => void;
  /** Open the drill-in on one group of people. */
  onDrill: (bucket: DrillBucket) => void;
  studies: StudyOption[];
  team: TeamMember[];
  forms: CrmForm[];
  /** Question words for definitions saved without them (the builder's lookup). */
  questionLabels?: DescribeCtx['questionLabels'];
}

const tinySelectCls = `${fieldCls} w-auto cursor-pointer !py-1 text-xs`;

export default function StepCard({
  step, stats, index, total, onChange, onRemove, onMove, onDrill, studies, team, forms, questionLabels,
}: Props) {
  // Freshly-added incomplete conditions start expanded for editing.
  const [editing, setEditing] = useState<number | null>(() => {
    const firstIncomplete = step.conditions.findIndex((c) => !isConditionReady(c));
    return firstIncomplete >= 0 ? firstIncomplete : null;
  });
  const [allWarnings, setAllWarnings] = useState(false);
  const ctx: DescribeCtx = { studies, team, forms, questionLabels };
  const question = isQuestionStep(step);
  const words = unknownWords(question);

  const setCondition = (i: number, next: SearchCondition) =>
    onChange({ ...step, conditions: step.conditions.map((c, j) => (j === i ? next : c)) });
  const removeCondition = (i: number) => {
    const conditions = step.conditions.filter((_, j) => j !== i);
    setEditing(null);
    if (conditions.length === 0) onRemove();
    else onChange({ ...step, conditions });
  };
  const addCondition = (c: SearchCondition) => {
    onChange({ ...step, conditions: [...step.conditions, c] });
    setEditing(step.conditions.length);
  };

  // Include keeps a no-data person only when told to; exclude keeps them unless told to drop.
  const keepsUnknown = step.mode === 'include' ? Boolean(step.keep_unknown) : !step.drop_unknown;
  const unknownControl = (
    <label className="inline-flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
      {words.people}:
      <select
        value={keepsUnknown ? 'keep' : 'drop'}
        onChange={(e) => {
          const keep = e.target.value === 'keep';
          onChange(step.mode === 'include'
            ? { ...step, keep_unknown: keep || undefined }
            : { ...step, drop_unknown: !keep || undefined });
        }}
        aria-label={words.people}
        className={tinySelectCls}
      >
        <option value="keep">keep them</option>
        <option value="drop">drop them</option>
      </select>
    </label>
  );

  const showCoverage = stats && stats.coverage_pct < 100;
  const warn = warningSummary(stats, question);

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 dark:border-gray-700 dark:bg-gray-800">
      {/* Header row */}
      <div className="flex items-center gap-1.5">
        <span className="inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
          {index + 1}
        </span>
        <select
          value={step.mode}
          onChange={(e) => onChange({ ...step, mode: e.target.value as 'include' | 'exclude', keep_unknown: undefined, drop_unknown: undefined })}
          aria-label="Keep or leave out"
          className={tinySelectCls}
        >
          <option value="include">Keep</option>
          <option value="exclude">Leave out</option>
        </select>
        {step.conditions.length > 1 && (
          <select
            value={step.match}
            onChange={(e) => onChange({ ...step, match: e.target.value as 'any' | 'all' })}
            aria-label="How the conditions combine"
            className={tinySelectCls}
          >
            <option value="any">people matching any</option>
            <option value="all">people matching all</option>
          </select>
        )}
        {/* The step's name — shown on the funnel bars, the run page and the outreach list's
            source instead of the first condition. Blank = describe it automatically. */}
        <input
          value={step.label || ''}
          onChange={(e) => onChange({ ...step, label: e.target.value ? e.target.value : null })}
          maxLength={80}
          placeholder={step.conditions.length ? describeStep({ ...step, label: null }, ctx) : 'Name this step (optional)'}
          aria-label="Step name"
          title="Name this step (optional)"
          className="min-w-0 flex-1 truncate rounded border-b border-transparent bg-transparent px-1 py-0.5 text-sm text-gray-800 outline-none placeholder:text-gray-400 hover:border-gray-200 focus:border-primary-400 dark:text-gray-100 dark:placeholder:text-gray-500 dark:hover:border-gray-600"
        />
        <button type="button" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Move step up" className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
          <ChevronUp className="h-4 w-4" />
        </button>
        <button type="button" onClick={() => onMove(1)} disabled={index === total - 1} aria-label="Move step down" className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 dark:hover:text-gray-200">
          <ChevronDown className="h-4 w-4" />
        </button>
        <button type="button" onClick={onRemove} aria-label="Remove step" className="rounded p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400">
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {/* Condition rows */}
      <ul className="mt-2 space-y-1.5">
        {step.conditions.map((c, i) => {
          const sentence = describeCondition(c, ctx);
          return (
            <li key={i} className="rounded-lg bg-gray-50 px-2 py-1.5 dark:bg-gray-700/50">
              <div className="flex items-center gap-1.5">
                <span className="min-w-0 flex-1 truncate text-sm text-gray-800 dark:text-gray-100" title={sentence}>
                  {i > 0 && <span className="mr-1 text-xs font-semibold text-gray-400">{step.match === 'all' ? 'and' : 'or'}</span>}
                  {sentence}
                  {!isConditionReady(c) && <span className="ml-1.5 text-xs text-amber-600 dark:text-amber-400">not finished</span>}
                </span>
                <button
                  type="button"
                  onClick={() => setEditing(editing === i ? null : i)}
                  aria-label={editing === i ? 'Close editor' : 'Edit condition'}
                  className={`rounded p-1 ${editing === i ? 'text-primary-600 dark:text-primary-400' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'}`}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button type="button" onClick={() => removeCondition(i)} aria-label="Remove condition" className="rounded p-1 text-gray-400 hover:text-rose-600 dark:hover:text-rose-400">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
              {editing === i && (
                <div className="mt-1.5 border-t border-gray-200 pt-1.5 dark:border-gray-600">
                  <CriterionEditor condition={c} onChange={(next) => setCondition(i, next)} studies={studies} team={team} forms={forms} />
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {/* Footer: add-condition + no-answer policy + share answered */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <CriterionPalette compact label="Add condition" onPick={addCondition} />
        {unknownControl}
        {showCoverage && (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${
              stats!.coverage_pct < 25
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
            }`}
            title={question
              ? 'Share of the people reaching this step who answered this question'
              : 'Share of the people reaching this step who have this on file'}
          >
            {stats!.coverage_pct}% {words.have}
          </span>
        )}
      </div>

      {stats && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 border-t border-gray-100 pt-2 text-xs dark:border-gray-700">
          <span className="tabular-nums text-gray-500 dark:text-gray-400">
            <span className="font-semibold text-gray-800 dark:text-gray-100">{stats.passing.toLocaleString()}</span> of {stats.entering.toLocaleString()} kept
          </span>
          <span className="mx-1 text-gray-300 dark:text-gray-600">·</span>
          <button type="button" onClick={() => onDrill('matched')} className="rounded px-1.5 py-0.5 font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-400 dark:hover:bg-primary-900/20">
            {stats.matched.toLocaleString()} matched
          </button>
          <button type="button" onClick={() => onDrill('excluded')} className="rounded px-1.5 py-0.5 font-medium text-rose-600 hover:bg-rose-50 dark:text-rose-400 dark:hover:bg-rose-900/20">
            {droppedWithData(stats, step).toLocaleString()} dropped
          </button>
          <button type="button" onClick={() => onDrill('no_data')} className="rounded px-1.5 py-0.5 font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700">
            {stats.no_data.toLocaleString()} {words.count}
          </button>
        </div>
      )}

      {/* The first warning, as a sentence — never hidden behind a hover count. */}
      {warn && (
        <div className="mt-1.5 flex items-start gap-1 text-xs text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
          <div className="min-w-0">
            {(allWarnings ? warn.all : [warn.first]).map((w) => <p key={w}>{w}</p>)}
            {warn.more > 0 && (
              <button
                type="button"
                onClick={() => setAllWarnings((v) => !v)}
                className="font-medium underline-offset-2 hover:underline"
              >
                {allWarnings ? 'Show less' : `and ${warn.more} more`}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
